'use client';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import s from './TreeMapHierarchy.module.scss';

const SortableLevelItem = ({
    id,
    value,
    index,
    onUpdate,
    onRemove,
    datasetFields,
    loadingFields,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className={s.LevelItem}>
            <button
                type="button"
                className={s.DragHandle}
                {...attributes}
                {...listeners}>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor">
                    <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z" />
                </svg>
            </button>

            <span className={s.LevelNumber}>{index + 1}</span>

            <select
                className={s.Select}
                value={value}
                onChange={(e) => onUpdate(id, e.target.value)}
                disabled={loadingFields}>
                <option value="">
                    {loadingFields ? 'Loading...' : 'Select property'}
                </option>
                {datasetFields.map((field) => (
                    <option key={field} value={field}>
                        {field}
                    </option>
                ))}
            </select>

            <button
                type="button"
                className={s.RemoveButton}
                onClick={() => onRemove(id)}
                title="Remove level">
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

const TreeMapHierarchy = ({
    levels = [],
    onChange,
    datasetFields,
    loadingFields,
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const generateId = () => Math.random().toString(36).substr(2, 9);

    // Ensure levels have IDs
    const normalizedLevels = levels.map((level) =>
        typeof level === 'string' ? { id: generateId(), value: level } : level
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = normalizedLevels.findIndex(
                (l) => l.id === active.id
            );
            const newIndex = normalizedLevels.findIndex(
                (l) => l.id === over.id
            );
            const reordered = arrayMove(normalizedLevels, oldIndex, newIndex);
            onChange(reordered.map((l) => l.value));
        }
    };

    const handleUpdate = (id, value) => {
        const updated = normalizedLevels.map((l) =>
            l.id === id ? { ...l, value } : l
        );
        onChange(updated.map((l) => l.value));
    };

    const handleRemove = (id) => {
        const filtered = normalizedLevels.filter((l) => l.id !== id);
        onChange(filtered.map((l) => l.value));
    };

    const handleAdd = () => {
        onChange([...levels, '']);
    };

    return (
        <div className={s.Root}>
            {normalizedLevels.length > 0 && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}>
                    <SortableContext
                        items={normalizedLevels.map((l) => l.id)}
                        strategy={verticalListSortingStrategy}>
                        <div className={s.LevelsList}>
                            {normalizedLevels.map((level, index) => (
                                <SortableLevelItem
                                    key={level.id}
                                    id={level.id}
                                    value={level.value}
                                    index={index}
                                    onUpdate={handleUpdate}
                                    onRemove={handleRemove}
                                    datasetFields={datasetFields}
                                    loadingFields={loadingFields}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            <button type="button" className={s.AddButton} onClick={handleAdd}>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                </svg>
                Add Level
            </button>
        </div>
    );
};

export default TreeMapHierarchy;
